import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { useClickSound } from '../hooks/useClickSound';
import { getProjects, getSuperteams } from '../utils/api';
import ProjectCreator from './ProjectCreator';
import ProjectViewer from './ProjectViewer';
import ProjectCard from './ProjectCard';
import type { Project, Superteam } from '../types';

type TabId = 'projects' | 'browse';

interface LeftPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LeftPanel({ isOpen, onClose }: LeftPanelProps) {
  const { isAuthenticated, user } = useAuth();
  const { withClickSound } = useClickSound();
  const [activeTab, setActiveTab] = useState<TabId>('browse');
  const [isMobile, setIsMobile] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showProjectCreator, setShowProjectCreator] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [superteams, setSuperteams] = useState<Superteam[]>([]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch superteams
  useEffect(() => {
    if (!isOpen) return;
    getSuperteams()
      .then(response => {
        if (response.superteams) {
          setSuperteams(response.superteams);
        }
      })
      .catch(err => console.error('Failed to fetch superteams:', err));
  }, [isOpen]);


  // Fetch all published projects for browsing
  const { data: projectsData, refetch: refetchProjects } = useQuery({
    queryKey: ['projects', 'published'],
    queryFn: () => getProjects({ status: 'published', limit: 50 }),
    enabled: isOpen,
  });

  // Fetch user's team projects (drafts and published)
  const { data: myProjectsData, refetch: refetchMyProjects } = useQuery({
    queryKey: ['myProjects'],
    queryFn: () => getProjects({ status: 'all', limit: 50 }),
    enabled: isAuthenticated && isOpen,
  });

  const publishedProjects = projectsData?.data?.projects || [];
  const myProjects = myProjectsData?.data?.projects || [];

  const handleRefresh = () => {
    refetchProjects();
    refetchMyProjects();
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setShowProjectCreator(true);
  };

  const handleCloseProjectViewer = () => {
    setSelectedProject(null);
  };

  const handleCloseProjectCreator = () => {
    setShowProjectCreator(false);
    setEditingProject(null);
    handleRefresh();
  };

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 40,
    transition: 'opacity 0.3s',
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? 'auto' : 'none',
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    zIndex: isMobile ? 201 : 50,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'rgba(10, 10, 20, 0.95)',
    backdropFilter: 'blur(30px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    ...(isMobile ? {
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: '20px 20px 0 0',
      maxHeight: '85vh',
      transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
    } : {
      top: '70px',
      left: '16px',
      bottom: '70px',
      width: '400px',
      borderRadius: '20px',
      transform: isOpen ? 'translateX(0)' : 'translateX(calc(-100% - 32px))',
    }),
  };

  const tabStyle = (isActive: boolean): CSSProperties => ({
    flex: 1,
    padding: '10px 12px',
    fontSize: '13px',
    fontWeight: 600,
    color: isActive ? 'white' : 'rgba(255,255,255,0.5)',
    background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />

      <div style={panelStyle}>
        {/* Header */}
        <div style={{ flexShrink: 0, padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {isMobile && (
            <div style={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', margin: '0 auto 16px' }} />
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #9945FF, #14F195)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}>
                🚀
              </div>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>Projects Hub</h2>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Projects</p>
              </div>
            </div>
            
            <button
              onClick={withClickSound(onClose)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', background: 'rgba(255,255,255,0.03)', padding: '4px', borderRadius: '12px' }}>
            <button
              onClick={withClickSound(() => setActiveTab('browse'))}
              style={tabStyle(activeTab === 'browse')}
            >
              Browse
            </button>
            {isAuthenticated && (
              <button
                onClick={withClickSound(() => setActiveTab('projects'))}
                style={tabStyle(activeTab === 'projects')}
              >
                My Projects
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {activeTab === 'browse' && (
            <BrowseTab
              projects={publishedProjects}
              onProjectClick={handleProjectClick}
            />
          )}

          {activeTab === 'projects' && isAuthenticated && (
            <MyProjectsTab
              projects={myProjects}
              onProjectClick={handleProjectClick}
              onCreateProject={() => {
                setEditingProject(null);
                setShowProjectCreator(true);
              }}
            />
          )}
        </div>
      </div>

      {/* Project Viewer Modal */}
      {selectedProject && (
        <ProjectViewer
          project={selectedProject}
          isOpen={!!selectedProject}
          onClose={handleCloseProjectViewer}
          onEdit={handleEditProject}
          canEdit={isAuthenticated && selectedProject.ownerId === user?.id}
        />
      )}

      {/* Project Creator Modal */}
      {showProjectCreator && (
        <ProjectCreator
          isOpen={showProjectCreator}
          onClose={handleCloseProjectCreator}
          editProject={editingProject}
          superteams={superteams}
        />
      )}
    </>
  );
}

// ==================== Sub-components ====================

interface BrowseTabProps {
  projects: Project[];
  onProjectClick: (project: Project) => void;
}

function BrowseTab({ projects, onProjectClick }: BrowseTabProps) {
  if (projects.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          fontSize: '24px',
        }}>
          🔍
        </div>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: 500 }}>No projects yet</p>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '4px' }}>Be the first to publish!</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>All Projects</span>
        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.05)', padding: '4px 8px', borderRadius: '6px' }}>
          {projects.length} projects
        </span>
      </div>
      {projects.map(project => (
        <ProjectCard key={project.id} project={project} onClick={() => onProjectClick(project)} />
      ))}
    </div>
  );
}

interface MyProjectsTabProps {
  projects: Project[];
  onProjectClick: (project: Project) => void;
  onCreateProject: () => void;
}

function MyProjectsTab({ projects, onProjectClick, onCreateProject }: MyProjectsTabProps) {
  const { withClickSound } = useClickSound();
  
  const drafts = projects.filter(p => p.status === 'draft');
  const published = projects.filter(p => p.status === 'published');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>My Projects</span>
        <button
          onClick={withClickSound(onCreateProject)}
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #9945FF, #14F195)',
            border: 'none',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          + New Project
        </button>
      </div>

      {/* Drafts */}
      {drafts.length > 0 && (
        <div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
            Drafts ({drafts.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {drafts.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => onProjectClick(project)}
                showStatus
              />
            ))}
          </div>
        </div>
      )}

      {/* Published */}
      {published.length > 0 && (
        <div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
            Published ({published.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {published.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => onProjectClick(project)}
                showStatus
              />
            ))}
          </div>
        </div>
      )}

      {projects.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '32px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px dashed rgba(255,255,255,0.1)',
          borderRadius: '12px',
        }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>No projects yet</p>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '4px' }}>Create your first project</p>
        </div>
      )}
    </div>
  );
}
